<template>
  <div class="stock-summary">
    <!-- Filters + actions -->
    <div class="rp-toolbar">
      <div class="rp-filters" style="--rp-cols: 4">
        <div class="rp-filter">
          <label>As on date</label>
          <input v-model="filters.date" type="date" class="form-control" :max="today" />
        </div>
        <div class="rp-filter">
          <label>Warehouse</label>
          <FormDropdown v-model="filters.warehouse" :options="warehouseOptions" placeholder="All warehouses" clearable />
        </div>
        <div class="rp-filter">
          <label>Category</label>
          <FormDropdown v-model="filters.category" :options="categoryOptions" placeholder="All categories" clearable />
        </div>
        <div class="rp-filter">
          <label>Subcategory</label>
          <FormDropdown
            v-model="filters.sub_category"
            :options="subCategoryOptions"
            placeholder="All subcategories"
            clearable
          />
        </div>
        <div class="rp-filter rp-search">
          <label>Item</label>
          <div class="position-relative">
            <i class="ti ti-search rp-search-icon"></i>
            <input
              v-model="filters.search"
              type="text"
              class="form-control"
              placeholder="Search name, code, SKU or barcode"
            />
          </div>
        </div>
      </div>

      <ReportActions
        :loading="loading"
        :exporting="exporting"
        :can-reset="hasActiveFilters"
        :can-print="!!rows.length"
        @reset="resetFilters"
        @export="exportFile"
        @print="printReport"
      />
    </div>

    <div v-if="error" class="alert alert-danger d-flex align-items-center gap-2">
      <i class="ti ti-alert-circle"></i>{{ error }}
    </div>

    <!-- Summary cards -->
    <div class="rp-cards" :class="{ 'is-loading': loading }">
      <div class="rp-card tone-blue">
        <span class="rp-card-icon"><i class="ti ti-packages"></i></span>
        <div>
          <div class="rp-card-label">Total Items</div>
          <div class="rp-card-value">{{ formatInt(summary.total_items) }}</div>
          <div class="rp-card-sub">
            {{ formatInt(summary.total_products) }} products · {{ formatInt(summary.total_warehouses) }}
            {{ summary.total_warehouses === 1 ? 'warehouse' : 'warehouses' }}
          </div>
        </div>
      </div>
      <div class="rp-card tone-teal">
        <span class="rp-card-icon"><i class="ti ti-stack-2"></i></span>
        <div>
          <div class="rp-card-label">Total Stock Quantity</div>
          <div class="rp-card-value">{{ formatQty(summary.total_quantity) }}</div>
          <div class="rp-card-sub">{{ isPastDate ? `On ${formatDate(filters.date)}` : 'Available units' }}</div>
        </div>
      </div>
      <div class="rp-card tone-orange">
        <span class="rp-card-icon"><i class="ti ti-coins"></i></span>
        <div>
          <div class="rp-card-label">Total Stock Value</div>
          <div class="rp-card-value">{{ formatMoney(summary.total_value) }}</div>
          <div class="rp-card-sub">Batch-wise cost</div>
        </div>
      </div>
      <button
        type="button"
        class="rp-card tone-red rp-card-action"
        :class="{ selected: statusFilter === 'attention' }"
        title="Show only low and out-of-stock items"
        @click="statusFilter = statusFilter === 'attention' ? 'all' : 'attention'"
      >
        <span class="rp-card-icon"><i class="ti ti-alert-triangle"></i></span>
        <div>
          <div class="rp-card-label">Low Stock Items</div>
          <div class="rp-card-value">{{ formatInt(summary.low_stock_items) }}</div>
          <div class="rp-card-sub">{{ formatInt(summary.out_of_stock_items) }} out of stock</div>
        </div>
      </button>
    </div>

    <!-- Insights -->
    <div class="rp-insights" :class="{ 'is-loading': loading }">
      <div class="rp-panel">
        <div class="rp-panel-title">Stock health</div>
        <div class="ss-health">
          <div class="ss-donut">
            <canvas ref="healthChart" width="132" height="132"></canvas>
            <div class="ss-donut-center">
              <strong>{{ healthyPercent }}%</strong>
              <small>healthy</small>
            </div>
          </div>
          <ul class="ss-legend">
            <li v-for="status in statusList" :key="status.key">
              <span class="dot" :style="{ background: status.color }"></span>
              <span class="flex-grow-1">{{ status.label }}</span>
              <strong>{{ formatInt(status.count) }}</strong>
            </li>
          </ul>
        </div>
      </div>

      <div class="rp-panel">
        <div class="rp-panel-title">Value by category</div>
        <div v-if="categoryBars.length" class="rp-bars">
          <div v-for="bar in categoryBars" :key="bar.name">
            <div class="d-flex justify-content-between small mb-1">
              <span class="text-truncate me-2">{{ bar.name }}</span>
              <span class="text-muted">{{ formatMoney(bar.value) }} · {{ bar.percent }}%</span>
            </div>
            <div class="rp-bar-track"><div class="rp-bar-fill" :style="{ width: `${bar.percent}%` }"></div></div>
          </div>
        </div>
        <p v-else class="text-muted small mb-0">No stock value for the current filters.</p>
      </div>

      <div class="rp-panel">
        <div class="rp-panel-title">Highlights</div>
        <ul class="rp-highlights">
          <li>
            <span class="hl-icon tone-orange"><i class="ti ti-trophy"></i></span>
            <div class="min-w-0">
              <small class="text-muted d-block">Highest value item</small>
              <span class="d-block text-truncate">{{ summary.top_item ? summary.top_item.name : '—' }}</span>
              <small v-if="summary.top_item" class="fw-semibold">{{ formatMoney(summary.top_item.value) }}</small>
            </div>
          </li>
          <li>
            <span class="hl-icon tone-blue"><i class="ti ti-building-warehouse"></i></span>
            <div class="min-w-0">
              <small class="text-muted d-block">Top warehouse by value</small>
              <span class="d-block text-truncate">{{ topWarehouse ? topWarehouse.name : '—' }}</span>
              <small v-if="topWarehouse" class="fw-semibold">{{ topWarehouse.percent }}% of stock value</small>
            </div>
          </li>
          <li>
            <span class="hl-icon tone-teal"><i class="ti ti-calculator"></i></span>
            <div class="min-w-0">
              <small class="text-muted d-block">Average value per item</small>
              <span class="d-block">{{ summary.total_items ? formatMoney(summary.total_value / summary.total_items) : '—' }}</span>
            </div>
          </li>
        </ul>
      </div>
    </div>

    <!-- Stock table -->
    <div class="rp-table-card">
      <div class="rp-table-head">
        <div class="rp-pills">
          <button
            v-for="pill in statusPills"
            :key="pill.key"
            type="button"
            class="rp-pill"
            :class="{ active: statusFilter === pill.key }"
            @click="statusFilter = pill.key"
          >
            {{ pill.label }} <span class="rp-pill-count">{{ formatInt(pill.count) }}</span>
          </button>
        </div>
        <div class="d-flex align-items-center gap-2 small text-muted">
          Show
          <select v-model.number="pageSize" class="form-select form-select-sm rp-page-size">
            <option v-for="size in PAGE_SIZES" :key="size" :value="size">{{ size }}</option>
          </select>
          per page
        </div>
      </div>

      <div class="table-responsive rp-table-wrap">
        <div v-if="loading" class="rp-loading"><span class="spinner-border text-warning"></span></div>
        <table class="table rp-table mb-0">
          <thead>
            <tr>
              <th class="rp-expand-col"></th>
              <th v-for="column in columns" :key="column.key" :class="column.class" @click="sortBy(column.key)">
                <span class="rp-sortable" :class="{ active: sortKey === column.key }">
                  {{ column.label }}
                  <i class="ti" :class="sortIcon(column.key)"></i>
                </span>
              </th>
            </tr>
          </thead>
          <tbody>
            <template v-for="row in pagedRows" :key="row.key">
              <tr :class="{ expanded: expanded[row.key] }">
                <td class="rp-expand-col">
                  <button
                    v-if="row.batches.length"
                    type="button"
                    class="rp-expand"
                    :title="expanded[row.key] ? 'Hide batches' : 'Show batches'"
                    @click="toggleRow(row.key)"
                  >
                    <i class="ti" :class="expanded[row.key] ? 'ti-chevron-down' : 'ti-chevron-right'"></i>
                  </button>
                </td>
                <td class="fw-semibold">{{ row.item_code }}</td>
                <td>
                  <div class="fw-medium">{{ row.item_name }}</div>
                  <small v-if="row.sku" class="text-muted">SKU: {{ row.sku }}</small>
                </td>
                <td>
                  <div>{{ row.category || '—' }}</div>
                  <small v-if="row.sub_category" class="text-muted">{{ row.sub_category }}</small>
                </td>
                <td>
                  <template v-if="row.variant">
                    <span v-for="part in row.variant.split(' · ')" :key="part" class="rp-variant">{{ part }}</span>
                  </template>
                  <span v-else class="text-muted">—</span>
                </td>
                <td>{{ row.warehouse }}</td>
                <td class="text-end">
                  <span class="fw-semibold">{{ formatQty(row.available_qty) }}</span>
                  <small v-if="row.unit" class="text-muted ms-1">{{ row.unit }}</small>
                  <div v-if="row.reorder_level" class="rp-hint">Reorder at {{ formatQty(row.reorder_level) }}</div>
                </td>
                <td class="text-end">
                  {{ row.unit_cost === null ? '—' : formatMoney(row.unit_cost) }}
                  <div v-if="row.batches.length > 1 && row.min_cost !== row.max_cost" class="rp-hint">
                    {{ row.batches.length }} batches · {{ formatMoney(row.min_cost) }}–{{ formatMoney(row.max_cost) }}
                  </div>
                </td>
                <td class="text-end fw-semibold">{{ formatMoney(row.stock_value) }}</td>
                <td><span class="ss-status" :class="row.status">{{ STATUS_LABELS[row.status] }}</span></td>
              </tr>

              <tr v-if="expanded[row.key]" class="rp-detail-row">
                <td></td>
                <td :colspan="columns.length">
                  <div class="rp-detail">
                    <div class="rp-detail-title"><i class="ti ti-box-seam me-1"></i>Batch details</div>
                    <table class="table table-sm mb-0">
                      <thead>
                        <tr>
                          <th>Batch No</th>
                          <th>Lot No</th>
                          <th>Store</th>
                          <th>In-house</th>
                          <th>Expiry</th>
                          <th class="text-end">Quantity</th>
                          <th class="text-end">Unit Cost</th>
                          <th class="text-end">Value</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr v-for="batch in row.batches" :key="batch.id">
                          <td class="fw-medium">{{ batch.batch_no || '—' }}</td>
                          <td>{{ batch.lot_no && batch.lot_no !== '000000' ? batch.lot_no : '—' }}</td>
                          <td>{{ batch.store || '—' }}</td>
                          <td>{{ formatDate(batch.in_house_date) }}</td>
                          <td :class="{ 'text-danger': isExpired(batch.expiry_date) }">{{ formatDate(batch.expiry_date) }}</td>
                          <td class="text-end">{{ formatQty(batch.quantity) }}</td>
                          <td class="text-end">{{ formatMoney(batch.unit_cost) }}</td>
                          <td class="text-end">{{ formatMoney(batch.value) }}</td>
                        </tr>
                      </tbody>
                      <tfoot>
                        <tr>
                          <th colspan="5" class="text-end">Total</th>
                          <th class="text-end">{{ formatQty(row.batch_qty) }}</th>
                          <th></th>
                          <th class="text-end">{{ formatMoney(row.stock_value) }}</th>
                        </tr>
                      </tfoot>
                    </table>
                  </div>
                </td>
              </tr>
            </template>

            <tr v-if="!loading && !pagedRows.length">
              <td :colspan="columns.length + 1">
                <div class="rp-empty">
                  <i class="ti ti-package-off"></i>
                  <div class="fw-semibold">No stock found</div>
                  <small class="text-muted">Try another date, warehouse or search.</small>
                </div>
              </td>
            </tr>
          </tbody>
          <tfoot v-if="visibleRows.length">
            <tr>
              <th></th>
              <th colspan="5" class="text-end">Total ({{ formatInt(visibleRows.length) }} rows)</th>
              <th class="text-end">{{ formatQty(visibleTotals.quantity) }}</th>
              <th></th>
              <th class="text-end">{{ formatMoney(visibleTotals.value) }}</th>
              <th></th>
            </tr>
          </tfoot>
        </table>
      </div>

      <ReportPagination
        v-if="visibleRows.length"
        :page="page"
        :page-count="pageCount"
        :pages="pageNumbers"
        :start="pageStart"
        :end="pageEnd"
        :total="visibleRows.length"
        @go="goToPage"
      />
    </div>
  </div>
</template>

<script>
import apiUrls from '@/urls';
import inventoryReport, { escapeHtml, localToday } from './inventoryReportMixin';

const STATUS_LABELS = { in_stock: 'In Stock', low_stock: 'Low Stock', out_of_stock: 'Out of Stock' };
const STATUS_COLORS = { in_stock: '#28C76F', low_stock: '#FE9F43', out_of_stock: '#EA5455' };

const EMPTY_SUMMARY = {
  total_items: 0, total_products: 0, total_warehouses: 0, total_quantity: 0, total_value: 0,
  low_stock_items: 0, out_of_stock_items: 0,
  status_counts: { in_stock: 0, low_stock: 0, out_of_stock: 0 },
  value_by_category: [], value_by_warehouse: [], top_item: null,
};

export default {
  name: 'StockSummaryReport',
  mixins: [inventoryReport],

  reportUrl: apiUrls.stock_summary_report.get,
  fileName: 'stock_summary',
  columns: [
    { key: 'item_code', label: 'Item Code' },
    { key: 'item_name', label: 'Item Name' },
    { key: 'category', label: 'Category' },
    { key: 'variant', label: 'Variant' },
    { key: 'warehouse', label: 'Warehouse' },
    { key: 'available_qty', label: 'Available Qty', class: 'text-end', numeric: true },
    { key: 'unit_cost', label: 'Unit Cost', class: 'text-end', numeric: true },
    { key: 'stock_value', label: 'Total Stock Value', class: 'text-end', numeric: true },
    { key: 'status', label: 'Status' },
  ],

  data() {
    const today = localToday();
    return {
      STATUS_LABELS,
      today,
      filters: { date: today, warehouse: null, category: null, sub_category: null, search: '' },
      summary: { ...EMPTY_SUMMARY },
      statusFilter: 'all',
      sortKey: 'item_name',
      sortDir: 'asc',
    };
  },

  computed: {
    isPastDate() {
      return !!this.filters.date && this.filters.date < this.today;
    },

    hasActiveFilters() {
      return this.isPastDate || this.itemFiltersActive;
    },

    statusList() {
      const counts = this.summary.status_counts || {};
      return Object.keys(STATUS_LABELS).map((key) => ({
        key, label: STATUS_LABELS[key], color: STATUS_COLORS[key], count: counts[key] || 0,
      }));
    },

    healthyPercent() {
      const total = this.statusList.reduce((sum, s) => sum + s.count, 0);
      return total ? Math.round(((this.summary.status_counts?.in_stock || 0) / total) * 100) : 0;
    },

    categoryBars() {
      const total = Number(this.summary.total_value) || 0;
      return (this.summary.value_by_category || [])
        .filter((c) => Number(c.value) > 0)
        .slice(0, 5)
        .map((c) => ({ ...c, percent: total ? Math.round((Number(c.value) / total) * 100) : 0 }));
    },

    topWarehouse() {
      const total = Number(this.summary.total_value) || 0;
      const top = (this.summary.value_by_warehouse || [])[0];
      if (!top || !Number(top.value)) return null;
      return { ...top, percent: total ? Math.round((Number(top.value) / total) * 100) : 0 };
    },

    statusPills() {
      const counts = this.summary.status_counts || {};
      return [
        { key: 'all', label: 'All', count: this.rows.length },
        { key: 'in_stock', label: 'In Stock', count: counts.in_stock || 0 },
        { key: 'attention', label: 'Low & Out', count: (counts.low_stock || 0) + (counts.out_of_stock || 0) },
        { key: 'out_of_stock', label: 'Out of Stock', count: counts.out_of_stock || 0 },
      ];
    },

    visibleRows() {
      let rows = this.rows;
      if (this.statusFilter === 'attention') {
        rows = rows.filter((r) => r.status !== 'in_stock');
      } else if (this.statusFilter !== 'all') {
        rows = rows.filter((r) => r.status === this.statusFilter);
      }
      return this.sortRows(rows);
    },

    visibleTotals() {
      return this.visibleRows.reduce(
        (totals, r) => ({
          quantity: totals.quantity + (Number(r.available_qty) || 0),
          value: totals.value + (Number(r.stock_value) || 0),
        }),
        { quantity: 0, value: 0 },
      );
    },
  },

  watch: {
    'filters.date'() { this.onFilterChange(); },
    statusFilter() { this.page = 1; },
  },

  methods: {
    extraParams() {
      return this.filters.date ? { date: this.filters.date } : {};
    },

    resetExtraFilters() {
      this.statusFilter = 'all';
      return { date: this.today };
    },

    applyResult(data) {
      this.summary = { ...EMPTY_SUMMARY, ...(data?.summary || {}) };
      this.$nextTick(this.renderChart);
    },

    renderChart() {
      const counts = this.statusList.map((s) => s.count);
      const empty = counts.every((count) => !count);
      this.drawChart('health', this.$refs.healthChart, {
        type: 'doughnut',
        data: {
          labels: this.statusList.map((s) => s.label),
          datasets: [{
            data: empty ? [1] : counts,
            backgroundColor: empty ? ['#EEF0F3'] : this.statusList.map((s) => s.color),
            borderWidth: 0,
          }],
        },
        options: {
          responsive: false,
          cutout: '72%',
          plugins: { legend: { display: false }, tooltip: { enabled: !empty } },
        },
      });
    },

    isExpired(value) {
      return !!value && new Date(value) < new Date(new Date().toDateString());
    },

    printReport() {
      const s = this.summary;
      const body = this.visibleRows.map((r) => `
        <tr>
          <td>${escapeHtml(r.item_code)}</td>
          <td>${escapeHtml(r.item_name)}${r.sku ? `<br><small>${escapeHtml(r.sku)}</small>` : ''}</td>
          <td>${escapeHtml(r.category)}</td>
          <td>${escapeHtml(r.variant)}</td>
          <td>${escapeHtml(r.warehouse)}</td>
          <td class="num">${this.formatQty(r.available_qty)}</td>
          <td class="num">${r.unit_cost === null ? '—' : this.formatMoney(r.unit_cost)}</td>
          <td class="num">${this.formatMoney(r.stock_value)}</td>
          <td class="${r.status}">${STATUS_LABELS[r.status]}</td>
        </tr>`).join('');
      const table = `<table><thead><tr><th>Item Code</th><th>Item Name</th><th>Category</th><th>Variant</th>
          <th>Warehouse</th><th class="num">Available Qty</th><th class="num">Unit Cost</th>
          <th class="num">Total Stock Value</th><th>Status</th></tr></thead>
        <tbody>${body}</tbody>
        <tfoot><tr><td colspan="5" class="num">Total</td><td class="num">${this.formatQty(this.visibleTotals.quantity)}</td>
          <td></td><td class="num">${this.formatMoney(this.visibleTotals.value)}</td><td></td></tr></tfoot></table>`;
      this.openPrint(
        'Stock Summary',
        [
          [`As on: ${this.formatDate(this.filters.date || this.today)}`, ...this.itemFiltersText()].join(' · '),
          `Printed ${new Date().toLocaleString()} · Stock value uses batch-wise costing`,
        ],
        [
          ['Total Items', this.formatInt(s.total_items)],
          ['Total Stock Quantity', this.formatQty(s.total_quantity)],
          ['Total Stock Value', this.formatMoney(s.total_value)],
          ['Low Stock Items', `${this.formatInt(s.low_stock_items)} (${this.formatInt(s.out_of_stock_items)} out of stock)`],
        ],
        table,
      );
    },
  },
};
</script>

<style scoped src="./inventoryReport.css"></style>

<style scoped>
.ss-health {
  display: flex;
  align-items: center;
  gap: 1.25rem;
}

.ss-donut {
  position: relative;
  flex: 0 0 132px;
  width: 132px;
  height: 132px;
}

.ss-donut-center {
  position: absolute;
  inset: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  pointer-events: none;
}

.ss-donut-center strong {
  color: #092C4C;
  font-size: 1.25rem;
}

.ss-donut-center small {
  color: #9AA1A9;
  font-size: 0.7rem;
}

.ss-legend {
  flex: 1;
  margin: 0;
  padding: 0;
  list-style: none;
}

.ss-legend li {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.35rem 0;
  font-size: 0.85rem;
  border-bottom: 1px dashed #EEF0F3;
}

.ss-legend li:last-child {
  border-bottom: 0;
}

.ss-legend .dot {
  width: 10px;
  height: 10px;
  border-radius: 50%;
}

.ss-status {
  display: inline-block;
  padding: 0.25rem 0.6rem;
  border-radius: 999px;
  font-size: 0.75rem;
  font-weight: 600;
  white-space: nowrap;
}

.ss-status.in_stock { background: rgba(40, 199, 111, 0.12); color: #1E9E5A; }
.ss-status.low_stock { background: rgba(254, 159, 67, 0.16); color: #D9771E; }
.ss-status.out_of_stock { background: rgba(234, 84, 85, 0.12); color: #D63939; }
</style>
